import React from "react";
import styles from "./LandingPage.module.css";

const LINKS = [
  ["What Are Cookies?", "#what-are-cookies"],
  ["How We Use Cookies", "#use"],
  ["Types of Cookies We Use", "#types"],
  ["Third-Party Cookies", "#third-party"],
  ["What We Do NOT Use Cookies For", "#no-use"],
  ["Managing Cookies", "#manage"],
  ["Changes to This Policy", "#changes"],
  ["Contact", "#contact"],
];

const CookiePolicy = () => {
  return (
    <main className={styles.main}>
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <header className={styles.legalHeader}>
            <div className={styles.sectionLabel}>LEGAL</div>
            <h1 className={styles.legalTitle}>Cookie Policy</h1>
            <p className={styles.legalMeta}>Last Updated: [Insert Date]</p>
          </header>

          <div className={styles.legalLayout}>
            <aside className={styles.legalSidebar}>
              <div className={styles.legalSidebarPanel}>
                <div className={styles.legalSidebarTitle}>On this page</div>
                <ul className={styles.legalSidebarList}>
                  {LINKS.map(([label, href]) => (
                    <li key={href}>
                      <a className={`${styles.legalSidebarLink} ${styles.focusRing}`} href={href}>
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className={styles.legalPanel}>
              <section id="intro">
                <p className={styles.legalBody}>
                  This Cookie Policy explains how the Platform uses cookies and similar technologies when you access
                  or use the application (“the Platform”).
                </p>
                <p className={styles.legalBody}>
                  By continuing to use the Platform, you consent to the use of cookies as described below.
                </p>
              </section>

              <section id="what-are-cookies" className={styles.legalClause}>
                <h2 className={styles.legalH2}>1. What Are Cookies?</h2>
                <p className={styles.legalBody}>
                  Cookies are small text files stored on your device by your browser when you visit a website. They
                  help websites function correctly, remember preferences, and understand how features are used.
                </p>
                <p className={styles.legalBody}>
                  Cookies do not give us access to your device or personal files.
                </p>
              </section>

              <section id="use" className={styles.legalClause}>
                <h2 className={styles.legalH2}>2. How We Use Cookies</h2>
                <p className={styles.legalBody}>
                  The Platform uses cookies only for functional and analytical purposes.
                </p>
                <p className={styles.legalBody}>Specifically, cookies help us:</p>
                <ul className={styles.legalList}>
                  <li>Maintain user sessions</li>
                  <li>Remember interface preferences (e.g., theme, layout options)</li>
                  <li>Improve performance and reliability</li>
                  <li>Understand aggregate usage patterns</li>
                  <li>Detect and prevent abuse or security issues</li>
                </ul>
                <p className={styles.legalBody}>We do not use cookies to:</p>
                <ul className={styles.legalList}>
                  <li>Track you across unrelated websites</li>
                  <li>Serve targeted advertising</li>
                  <li>Build marketing profiles</li>
                  <li>Sell or share data with advertisers</li>
                </ul>
              </section>

              <section id="types" className={styles.legalClause}>
                <h2 className={styles.legalH2}>3. Types of Cookies We Use</h2>
                <p className={styles.legalBody}>A. Strictly Necessary Cookies</p>
                <p className={styles.legalBody}>
                  These cookies are required for the Platform to function and cannot be disabled without impacting
                  core features.
                </p>
                <p className={styles.legalBody}>Examples:</p>
                <ul className={styles.legalList}>
                  <li>Authentication/session cookies</li>
                  <li>Security-related cookies</li>
                  <li>Load-balancing cookies</li>
                </ul>
                <p className={styles.legalBody}>B. Functional Cookies</p>
                <p className={styles.legalBody}>These cookies remember your preferences and improve usability.</p>
                <p className={styles.legalBody}>Examples:</p>
                <ul className={styles.legalList}>
                  <li>Theme (dark/light) preference</li>
                  <li>UI configuration or layout settings</li>
                  <li>Dismissed notices or onboarding steps</li>
                </ul>
                <p className={styles.legalBody}>C. Analytical Cookies</p>
                <p className={styles.legalBody}>
                  We may use limited analytics cookies to understand how features are used in aggregate, such as:
                </p>
                <ul className={styles.legalList}>
                  <li>Page views</li>
                  <li>Feature interaction frequency</li>
                  <li>Performance diagnostics</li>
                </ul>
                <p className={styles.legalBody}>Analytics data is:</p>
                <ul className={styles.legalList}>
                  <li>Aggregated</li>
                  <li>Anonymized where possible</li>
                  <li>Used solely for product improvement</li>
                </ul>
                <p className={styles.legalBody}>
                  We do not use analytics cookies for advertising or behavioral targeting.
                </p>
              </section>

              <section id="third-party" className={styles.legalClause}>
                <h2 className={styles.legalH2}>4. Third-Party Cookies</h2>
                <p className={styles.legalBody}>
                  The Platform may rely on a small number of trusted third-party service providers for:
                </p>
                <ul className={styles.legalList}>
                  <li>Hosting</li>
                  <li>Performance monitoring</li>
                  <li>Error tracking</li>
                </ul>
                <p className={styles.legalBody}>
                  These providers may set cookies only to support Platform functionality and are contractually
                  restricted from using data for their own purposes.
                </p>
              </section>

              <section id="no-use" className={styles.legalClause}>
                <h2 className={styles.legalH2}>5. What We Do NOT Use Cookies For</h2>
                <p className={styles.legalBody}>To be explicit, we do not use cookies for:</p>
                <ul className={styles.legalList}>
                  <li>Targeted or behavioral advertising</li>
                  <li>Cross-site tracking</li>
                  <li>Retargeting campaigns</li>
                  <li>Data resale</li>
                </ul>
                <p className={styles.legalBody}>
                  The Platform is designed as a professional analytics tool, not an advertising platform.
                </p>
              </section>

              <section id="manage" className={styles.legalClause}>
                <h2 className={styles.legalH2}>6. Managing Cookies</h2>
                <p className={styles.legalBody}>
                  You can control or delete cookies through your browser settings.
                </p>
                <p className={styles.legalBody}>Please note:</p>
                <ul className={styles.legalList}>
                  <li>Disabling cookies may limit certain features</li>
                  <li>Strictly necessary cookies are required for core functionality</li>
                </ul>
                <p className={styles.legalBody}>Browser instructions:</p>
                <ul className={styles.legalList}>
                  <li>Chrome: Settings → Privacy &amp; Security → Cookies</li>
                  <li>Safari: Preferences → Privacy</li>
                  <li>Firefox: Settings → Privacy &amp; Security</li>
                </ul>
              </section>

              <section id="changes" className={styles.legalClause}>
                <h2 className={styles.legalH2}>7. Changes to This Policy</h2>
                <p className={styles.legalBody}>
                  We may update this Cookie Policy from time to time.
                </p>
                <p className={styles.legalBody}>
                  When changes are made, the “Last Updated” date will be revised. Continued use of the Platform
                  indicates acceptance of the updated policy.
                </p>
              </section>

              <section id="contact" className={styles.legalClause}>
                <h2 className={styles.legalH2}>8. Contact</h2>
                <p className={styles.legalBody}>
                  If you have questions about our use of cookies, contact:
                </p>
                <p className={styles.legalBody}>Deng Software LLC</p>
                <p className={styles.legalBody}>info@deng-consulting.com</p>
              </section>
            </div>
          </div>

          <div className={styles.legalFooter}>
            Educational analytics only. Not financial advice. No trade execution.
          </div>
        </div>
      </section>
    </main>
  );
};

export default CookiePolicy;
